import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import Dropzone from 'react-dropzone';
import classnames from 'classnames';

import SingleAttachment from '../singleAttachment.component';

const DOCUMENT_ACCEPT_TYPES = ['.xls', '.xlsx', '.doc', '.docx', '.ppt', '.pptx', '.pdf', '.txt'];
const maxAttachmentSizeInKiloBytes = sizeInKilobytes => sizeInKilobytes * 1024;
const maxAttachmentSizeInBytes = sizeInMegabytes => maxAttachmentSizeInKiloBytes(sizeInMegabytes) * 1024;
const Errors = {
  CANNOT_UPLOAD_THIS_FILE: 'Cannot upload this file',
  MAXIMUM_FILE_SIZE: (maxFileSize = 1) => `Maximum file size is ${maxFileSize}MB`,
  FILE_ALREADY_UPLOADED: 'This file was already uploaded',
  FILE_READING_ABORTED: 'File reading was aborted',
  FILE_READING_FAILED: 'File reading has failed',
};

class Attachments extends PureComponent {
  static propTypes = {
    acceptTypes: PropTypes.arrayOf(PropTypes.string),
    addFileToForm: PropTypes.func.isRequired,
    removeFileFromForm: PropTypes.func,
    files: PropTypes.arrayOf(PropTypes.shape({
      fileName: PropTypes.string.isRequired,
      fileImage: PropTypes.string.isRequired,
      fileType: PropTypes.string.isRequired,
    })).isRequired,
    maxFileSize: PropTypes.number,
    required: PropTypes.bool,
    disabled: PropTypes.bool,
    showErrorByForce: PropTypes.bool,
    headerText: PropTypes.string,
    horizontal: PropTypes.bool,
    formError: PropTypes.string,
    showImage: PropTypes.bool,
    showShortDescription: PropTypes.bool,
    customAttachmentDescription: PropTypes.element,
    getRawFile: PropTypes.func,
  };

  static defaultProps = {
    acceptTypes: DOCUMENT_ACCEPT_TYPES,
    removeFileFromForm: null,
    maxFileSize: 0,
    headerText: 'Attachments',
    horizontal: false,
    formError: '',
    showImage: false,
    showShortDescription: false,
    customAttachmentDescription: null,
    getRawFile: null,
    disabled: false,
  };

  state = {
    error: '',
  };

  componentDidUpdate() {
    const fileListIsEmpty = !this.props.files.length;
    const shouldShowError = this.props.showErrorByForce;
    const existingError = this.state.error;

    if (shouldShowError && fileListIsEmpty && !existingError) {
      this.setState({ error: 'Required.' });
    }
  }

  addFile = async files => {
    const {
      maxFileSize,
      acceptTypes,
      getRawFile,
    } = this.props;

    const file = files[0];

    if (!file) {
      this.setState({ error: Errors.CANNOT_UPLOAD_THIS_FILE });
      return;
    }

    if (maxFileSize && file.size > maxAttachmentSizeInBytes(maxFileSize)) {
      this.setState({ error: Errors.MAXIMUM_FILE_SIZE(maxFileSize) });
      return;
    }

    const fileExtension = file.name.split('.').pop();
    if (!acceptTypes.includes(`.${fileExtension}`)) {
      this.setState({ error: Errors.CANNOT_UPLOAD_THIS_FILE });
      return;
    }

    if (this.props.files.find(({ fileName }) => file.name === fileName)) {
      this.setState({ error: Errors.FILE_ALREADY_UPLOADED });
      return;
    }

    if (!getRawFile) {
      const reader = new FileReader();
      reader.onload = () => {
        const fileAsDataUrl = reader.result;

        this.props.addFileToForm({
          fileName: file.name,
          fileType: fileExtension,
          fileImage: fileAsDataUrl.split('base64,')[1],
          fileUrl: fileAsDataUrl,
        });

        this.setState({ error: '' });
      };
      reader.onabort = () => this.setState({ error: Errors.FILE_READING_ABORTED });
      reader.onerror = () => this.setState({ error: Errors.FILE_READING_FAILED });

      reader.readAsDataURL(file);
    } else {
      getRawFile(file);
    }
  };

  removeFile = fileToRemove => {
    this.props.removeFileFromForm(fileToRemove);
  };

  render() {
    const {
      acceptTypes,
      files,
      maxFileSize,
      required,
      horizontal,
      formError,
      showImage,
      showShortDescription,
      customAttachmentDescription,
      disabled,
    } = this.props;
    const { error } = this.state;
    const acceptTypesText = acceptTypes.join(', ');

    const attachmentClassName = classnames('attachments', { 'row align-items-center': horizontal } );
    const labelClassName = classnames({ 'col-3': horizontal });
    const dropzoneClassName = classnames({ 'col-9': horizontal });

    const attachmentDescription = customAttachmentDescription
      ? customAttachmentDescription
      : (
        <div className="attachment-upload-hint">
          <p>Drop some files here, or <span>click</span> to select files to upload.</p>
          { !showShortDescription
              && <p>Only {acceptTypesText} files will be accepted.</p>
          }
          { !showShortDescription && maxFileSize
            ? <p>Maximum upload size is {maxFileSize} MB ({maxAttachmentSizeInKiloBytes(maxFileSize)}K).</p>
            : null
          }
        </div>
      );

    return (
      <div className={attachmentClassName}>
        <div className={labelClassName}>
          {this.props.headerText}{required ? <span className="form-asterisk"> *</span> : ''}
        </div>
        <div className={dropzoneClassName}>
          <Dropzone
            accept={acceptTypesText}
            onDrop={this.addFile}
            disabled={disabled}
          >
            {({ getRootProps, getInputProps }) => (
              <div {...getRootProps()} className="attachment-upload">
                <input {...getInputProps()}/>
                {(formError || error)
                  ? <span className="upload-error">{(formError || error)}</span>
                  : attachmentDescription}
              </div>
            )}
          </Dropzone>
        </div>
        <div className="attachments-wrapper">
          {
            files.map(file => (
              <SingleAttachment
                file={file}
                key={file.fileName}
                removeFile={this.removeFile}
                showImage={showImage}
                disabled={disabled}
              />
            ))
          }
        </div>
      </div>
    );
  }
}

export default Attachments;
